import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  message, 
  Popconfirm, 
  Tag, 
  Space,
  Card,
  Descriptions,
  Select,
  Dropdown
} from 'antd';
import { 
  DeleteOutlined, 
  EyeOutlined,
  ReloadOutlined,
  MobileOutlined,
  AppstoreOutlined,
  DownOutlined
} from '@ant-design/icons';
import api from '../utils/api';

const { Option } = Select;

const DeviceList = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [apks, setApks] = useState([]);
  const [installVisible, setInstallVisible] = useState(false);
  const [uninstallVisible, setUninstallVisible] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [uninstalling, setUninstalling] = useState(false);
  const [editingDeviceName, setEditingDeviceName] = useState(false);
  const [deviceNameValue, setDeviceNameValue] = useState('');
  const [savingDeviceName, setSavingDeviceName] = useState(false);
  const [editingRemark, setEditingRemark] = useState(false);
  const [remarkValue, setRemarkValue] = useState('');
  const [savingRemark, setSavingRemark] = useState(false);
  const [installedAppsVisible, setInstalledAppsVisible] = useState(false);
  const [uninstallingApp, setUninstallingApp] = useState(false);
  const [form] = Form.useForm();

  const isDeviceOnline = (lastSeen) => {
    if (!lastSeen) return false;
    const lastSeenTime = new Date(lastSeen).getTime();
    const now = Date.now();
    const oneHour = 60 * 60 * 1000;
    return (now - lastSeenTime) < oneHour;
  };

  const getDeviceStatus = (lastSeen) => {
    return isDeviceOnline(lastSeen) ? 'online' : 'offline';
  };

  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = async () => {
    setLoading(true);
    try {
      const response = await api.get('/devices');
      setDevices(response.data.data || []);
    } catch (error) {
      message.error('获取设备列表失败');
      console.error('Fetch devices error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAPKs = async () => {
    try {
      const response = await api.get('/apks');
      setApks(response.data.data || []);
    } catch (error) {
      message.error('获取APK列表失败');
      console.error('Fetch APKs error:', error);
    }
  };

  const handleDelete = async (deviceId) => {
    try {
      await api.delete(`/devices/${deviceId}`);
      message.success('设备删除成功');
      fetchDevices();
    } catch (error) {
      message.error('设备删除失败');
      console.error('Delete device error:', error);
    }
  };

  const showDetail = (device) => {
    setSelectedDevice(device);
    setDetailVisible(true);
  };

  const showInstallModal = async (device) => {
    setSelectedDevice(device);
    await fetchAPKs();
    setInstallVisible(true);
  };

  const showUninstallModal = (device) => {
    setSelectedDevice(device);
    setUninstallVisible(true);
  };

  const handleInstall = async (values) => {
    if (!selectedDevice) {
      message.error('请选择设备');
      return;
    }

    setInstalling(true);
    try {
      const response = await api.post('/commands/install', {
        deviceId: selectedDevice.deviceId,
        apkId: values.apkId,
        packageName: values.packageName
      });

      if (response.data.success) {
        message.success('安装指令已发送');
        setInstallVisible(false);
        form.resetFields();
      } else {
        message.error(response.data.message || '发送安装指令失败');
      }
    } catch (error) {
      message.error('发送安装指令失败');
      console.error('Install error:', error);
    } finally {
      setInstalling(false);
    }
  };

  const handleUninstall = async (values) => {
    if (!selectedDevice) {
      message.error('请选择设备');
      return;
    }

    setUninstalling(true);
    try {
      const response = await api.post('/commands/uninstall', {
        deviceId: selectedDevice.deviceId,
        packageName: values.packageName
      });

      if (response.data.success) {
        message.success('卸载指令已发送');
        setUninstallVisible(false);
        form.resetFields();
      } else {
        message.error(response.data.message || '发送卸载指令失败');
      }
    } catch (error) {
      message.error('发送卸载指令失败');
      console.error('Uninstall error:', error);
    } finally {
      setUninstalling(false);
    }
  };

  const handleEditRemark = () => {
    setRemarkValue(selectedDevice?.remark || '');
    setEditingRemark(true);
  };

  const handleSaveRemark = async () => {
    if (!selectedDevice) return;

    setSavingRemark(true);
    try {
      const response = await api.put(`/devices/${selectedDevice.deviceId}/remark`, {
        remark: remarkValue
      });

      if (response.data.success) {
        message.success('备注保存成功');
        setSelectedDevice(response.data.data);
        setEditingRemark(false);
        fetchDevices();
      } else {
        message.error(response.data.message || '保存备注失败');
      }
    } catch (error) {
      message.error('保存备注失败');
      console.error('Save remark error:', error);
    } finally {
      setSavingRemark(false);
    }
  };

  const handleCancelRemark = () => {
    setEditingRemark(false);
    setRemarkValue(selectedDevice?.remark || '');
  };

  const handleEditDeviceName = () => {
    setDeviceNameValue(selectedDevice?.deviceName || '');
    setEditingDeviceName(true);
  };

  const handleSaveDeviceName = async () => {
    if (!selectedDevice) return;

    if (!deviceNameValue || deviceNameValue.trim() === '') {
      message.error('设备名称不能为空');
      return;
    }

    setSavingDeviceName(true);
    try {
      const response = await api.put(`/devices/${selectedDevice.deviceId}`, {
        deviceName: deviceNameValue.trim()
      });

      if (response.data.success) {
        message.success('设备名称保存成功');
        setSelectedDevice(response.data.data);
        setEditingDeviceName(false);
        fetchDevices();
      } else {
        message.error(response.data.message || '保存设备名称失败');
      }
    } catch (error) {
      message.error('保存设备名称失败');
      console.error('Save device name error:', error);
    } finally {
      setSavingDeviceName(false);
    }
  };

  const handleCancelDeviceName = () => {
    setEditingDeviceName(false);
    setDeviceNameValue(selectedDevice?.deviceName || '');
  };

  const showInstalledAppsModal = (device) => {
    setSelectedDevice(device);
    setInstalledAppsVisible(true);
  };

  const handleUninstallApp = async (packageName) => {
    if (!selectedDevice) return;

    setUninstallingApp(true);
    try {
      const response = await api.post('/commands/uninstall', {
        deviceId: selectedDevice.deviceId,
        packageName
      });

      if (response.data.success) {
        message.success('卸载指令已发送');
        
        const updatedApps = (selectedDevice.installedApps || []).filter(
          app => app.packageName !== packageName
        );
        
        const updatedDevice = { ...selectedDevice, installedApps: updatedApps };
        setSelectedDevice(updatedDevice);
        
        const updatedDevices = devices.map(d => 
          d.deviceId === updatedDevice.deviceId ? updatedDevice : d
        );
        setDevices(updatedDevices);
        
        try {
          await api.put(`/devices/${selectedDevice.deviceId}/installed-apps`, {
            installedApps: updatedApps
          });
        } catch (error) {
          console.error('Update installed apps error:', error);
        }
      } else {
        message.error(response.data.message || '发送卸载指令失败');
      }
    } catch (error) {
      message.error('发送卸载指令失败');
      console.error('Uninstall app error:', error);
    } finally {
      setUninstallingApp(false);
    }
  };

  const columns = [
    {
      title: '设备名称',
      dataIndex: 'deviceName',
      key: 'deviceName',
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => showDetail(record)}
          style={{ padding: 0 }}
        >
          {text}
        </Button>
      )
    },
    {
      title: '设备型号',
      dataIndex: 'deviceModel',
      key: 'deviceModel'
    },
    {
      title: 'Android版本',
      dataIndex: 'androidVersion',
      key: 'androidVersion'
    },
    {
      title: '应用版本',
      dataIndex: 'appVersion',
      key: 'appVersion'
    },
    {
      title: '已安装应用数',
      dataIndex: 'installedApps',
      key: 'installedApps',
      render: (installedApps, record) => (
        <Button
          type="link"
          onClick={() => showInstalledAppsModal(record)}
        >
          {installedApps?.length || 0}
        </Button>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        const actualStatus = getDeviceStatus(record.lastSeen);
        return (
          <Tag color={actualStatus === 'online' ? 'green' : 'red'}>
            {actualStatus === 'online' ? '在线' : '离线'}
          </Tag>
        );
      }
    },
    {
      title: '设备所有者',
      dataIndex: 'isDeviceOwner',
      key: 'isDeviceOwner',
      render: (isOwner) => (
        <Tag color={isOwner ? 'blue' : 'default'}>
          {isOwner ? '已授权' : '未授权'}
        </Tag>
      )
    },
    {
      title: '最后在线',
      dataIndex: 'lastSeen',
      key: 'lastSeen',
      render: (date) => new Date(date).toLocaleString('zh-CN')
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'detail',
                icon: <EyeOutlined />,
                label: '详情',
                onClick: () => showDetail(record)
              },
              {
                key: 'install',
                icon: <AppstoreOutlined />,
                label: '安装应用',
                onClick: () => showInstallModal(record)
              },
              {
                key: 'uninstall',
                icon: <MobileOutlined />,
                label: '卸载应用',
                onClick: () => showUninstallModal(record)
              },
              {
                type: 'divider'
              },
              {
                key: 'delete',
                icon: <DeleteOutlined />,
                label: '删除',
                danger: true,
                onClick: () => {
                  Modal.confirm({
                    title: '确定要删除这个设备吗？',
                    content: '删除后将无法恢复',
                    okText: '确定',
                    cancelText: '取消',
                    okButtonProps: { danger: true },
                    onOk: () => handleDelete(record.deviceId)
                  });
                }
              }
            ]
          }}
          trigger={['hover']}
        >
          <Button>
            操作 <DownOutlined />
          </Button>
        </Dropdown>
      )
    }
  ];

  return (
    <div>
      <Card
        title="设备管理"
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchDevices}
            loading={loading}
          >
            刷新
          </Button>
        }
      >
        <Table
          dataSource={devices}
          columns={columns}
          rowKey="deviceId"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 台设备`
          }}
        />
      </Card>

      <Modal
        title="设备详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailVisible(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        {selectedDevice && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="设备ID" span={2}>
              {selectedDevice.deviceId}
            </Descriptions.Item>
            <Descriptions.Item label="设备名称">
              {editingDeviceName ? (
                <Space.Compact style={{ width: '100%' }}>
                  <Input
                    value={deviceNameValue}
                    onChange={(e) => setDeviceNameValue(e.target.value)}
                    placeholder="请输入设备名称"
                  />
                  <Button type="primary" onClick={handleSaveDeviceName} loading={savingDeviceName}>
                    保存
                  </Button>
                  <Button onClick={handleCancelDeviceName}>
                    取消
                  </Button>
                </Space.Compact>
              ) : (
                <Space>
                  <span>{selectedDevice.deviceName}</span>
                  <Button type="link" size="small" onClick={handleEditDeviceName}>
                    编辑
                  </Button>
                </Space>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="设备型号">
              {selectedDevice.deviceModel}
            </Descriptions.Item>
            <Descriptions.Item label="Android版本">
              {selectedDevice.androidVersion}
            </Descriptions.Item>
            <Descriptions.Item label="应用版本">
              {selectedDevice.appVersion}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={getDeviceStatus(selectedDevice.lastSeen) === 'online' ? 'green' : 'red'}>
                {getDeviceStatus(selectedDevice.lastSeen) === 'online' ? '在线' : '离线'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="设备所有者">
              <Tag color={selectedDevice.isDeviceOwner ? 'blue' : 'default'}>
                {selectedDevice.isDeviceOwner ? '已授权' : '未授权'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="电池电量">
              {selectedDevice.battery ? `${selectedDevice.battery}%` : '未知'}
            </Descriptions.Item>
            <Descriptions.Item label="存储空间">
              {selectedDevice.storage ? `${selectedDevice.storage}` : '未知'}
            </Descriptions.Item>
            <Descriptions.Item label="备注" span={2}>
              {editingRemark ? (
                <Space.Compact style={{ width: '100%' }}>
                  <Input.TextArea
                    value={remarkValue}
                    onChange={(e) => setRemarkValue(e.target.value)}
                    maxLength={200}
                    showCount
                    autoSize={{ minRows: 2, maxRows: 4 }}
                    placeholder="请输入备注信息"
                  />
                  <Button type="primary" onClick={handleSaveRemark} loading={savingRemark}>
                    保存
                  </Button>
                  <Button onClick={handleCancelRemark}>
                    取消
                  </Button>
                </Space.Compact>
              ) : (
                <Space>
                  <span>{selectedDevice.remark || '无'}</span>
                  <Button type="link" size="small" onClick={handleEditRemark}>
                    编辑
                  </Button>
                </Space>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="注册时间" span={2}>
              {new Date(selectedDevice.createdAt).toLocaleString('zh-CN')}
            </Descriptions.Item>
            <Descriptions.Item label="最后在线" span={2}>
              {new Date(selectedDevice.lastSeen).toLocaleString('zh-CN')}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <Modal
        title="安装应用"
        open={installVisible}
        onCancel={() => setInstallVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setInstallVisible(false)}>
            取消
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            loading={installing}
            onClick={() => form.submit()}
          >
            发送安装指令
          </Button>
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleInstall}
        >
          <Form.Item
            label="设备"
          >
            <Input value={selectedDevice?.deviceName} disabled />
          </Form.Item>
          <Form.Item
            label="选择APK"
            name="apkId"
            rules={[{ required: true, message: '请选择APK' }]}
          >
            <Select placeholder="请选择要安装的APK">
              {apks.map(apk => (
                <Option key={apk.apkId} value={apk.apkId}>
                  {apk.fileName} ({apk.versionName})
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="包名"
            name="packageName"
            rules={[{ required: true, message: '请输入包名' }]}
          >
            <Input placeholder="例如: com.example.app" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="卸载应用"
        open={uninstallVisible}
        onCancel={() => setUninstallVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setUninstallVisible(false)}>
            取消
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            loading={uninstalling}
            onClick={() => form.submit()}
          >
            发送卸载指令
          </Button>
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUninstall}
        >
          <Form.Item
            label="设备"
          >
            <Input value={selectedDevice?.deviceName} disabled />
          </Form.Item>
          <Form.Item
            label="包名"
            name="packageName"
            rules={[{ required: true, message: '请输入包名' }]}
          >
            <Input placeholder="例如: com.example.app" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`已安装应用 - ${selectedDevice?.deviceName}`}
        open={installedAppsVisible}
        onCancel={() => setInstalledAppsVisible(false)}
        footer={[
          <Button key="close" onClick={() => setInstalledAppsVisible(false)}>
            关闭
          </Button>
        ]}
        width={1000}
      >
        {selectedDevice && (
          <Table
            dataSource={selectedDevice.installedApps || []}
            rowKey="packageName"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 个应用`
            }}
            columns={[
              {
                title: '应用名称',
                dataIndex: 'appName',
                key: 'appName',
                ellipsis: true
              },
              {
                title: '包名',
                dataIndex: 'packageName',
                key: 'packageName',
                ellipsis: true
              },
              {
                title: '版本号',
                dataIndex: 'version',
                key: 'version'
              },
              {
                title: '安装时间',
                dataIndex: 'installDate',
                key: 'installDate',
                render: (date) => date ? new Date(date).toLocaleString('zh-CN') : '未知'
              },
              {
                title: '操作',
                key: 'action',
                render: (_, record) => (
                  <Popconfirm
                    title="确定要卸载这个应用吗？"
                    description={`应用名称: ${record.appName}`}
                    onConfirm={() => handleUninstallApp(record.packageName)}
                    okText="确定"
                    cancelText="取消"
                    okButtonProps={{ danger: true }}
                  >
                    <Button
                      type="primary"
                      danger
                      size="small"
                      loading={uninstallingApp}
                    >
                      卸载
                    </Button>
                  </Popconfirm>
                )
              }
            ]}
          />
        )}
      </Modal>
    </div>
  );
};

export default DeviceList;
